import React from "react";
import { motion } from "motion/react";

// =================================================================
// ANIMAÇÕES COMPARTILHADAS
// =================================================================

export const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      staggerChildren: 0.05,
    },
  },
};

export const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4 },
  },
};

export const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
  hover: {
    backgroundColor: "rgba(59, 130, 246, 0.05)",
    transition: { duration: 0.2 },
  },
};

export const cardVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.3 },
  },
  hover: {
    scale: 1.02,
    transition: { duration: 0.2 },
  },
};

// =================================================================
// TIPOS COMPARTILHADOS
// =================================================================

export interface ColorScheme {
  from: string;
  to: string;
  darkFrom: string;
  darkTo: string;
  border: string;
  darkBorder: string;
  iconBg: string;
  darkIconBg: string;
  iconColor: string;
  darkIconColor: string;
  labelColor: string;
  darkLabelColor: string;
  valueColor: string;
  darkValueColor: string;
}

export interface InfoCardData {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  colorScheme: ColorScheme;
}

// =================================================================
// SISTEMA DE CORES SIMPLIFICADO
// =================================================================

export const colorSchemes: ColorScheme[] = [
  {
    from: "from-blue-50",
    to: "to-cyan-50",
    darkFrom: "dark:from-blue-950/30",
    darkTo: "dark:to-blue-950/30",
    border: "border-blue-200",
    darkBorder: "dark:border-blue-800",
    iconBg: "bg-blue-100",
    darkIconBg: "dark:bg-blue-900/50",
    iconColor: "text-blue-600",
    darkIconColor: "dark:text-blue-400",
    labelColor: "text-blue-600",
    darkLabelColor: "dark:text-blue-400",
    valueColor: "text-blue-800",
    darkValueColor: "dark:text-blue-200",
  },
  {
    from: "from-emerald-50",
    to: "to-green-50",
    darkFrom: "dark:from-emerald-950/30",
    darkTo: "dark:to-emerald-950/30",
    border: "border-emerald-200",
    darkBorder: "dark:border-emerald-800",
    iconBg: "bg-emerald-100",
    darkIconBg: "dark:bg-emerald-900/50",
    iconColor: "text-emerald-600",
    darkIconColor: "dark:text-emerald-400",
    labelColor: "text-emerald-600",
    darkLabelColor: "dark:text-emerald-400",
    valueColor: "text-emerald-800",
    darkValueColor: "dark:text-emerald-200",
  },
  {
    from: "from-orange-50",
    to: "to-amber-50",
    darkFrom: "dark:from-orange-950/30",
    darkTo: "dark:to-amber-950/30",
    border: "border-orange-200",
    darkBorder: "dark:border-orange-800",
    iconBg: "bg-orange-100",
    darkIconBg: "dark:bg-orange-900/50",
    iconColor: "text-orange-600",
    darkIconColor: "dark:text-orange-400",
    labelColor: "text-orange-600",
    darkLabelColor: "dark:text-orange-400",
    valueColor: "text-orange-800",
    darkValueColor: "dark:text-orange-200",
  },
];

// Alias para compatibilidade
export const produtosColorSchemes = colorSchemes;

// =================================================================
// COMPONENTES COMPARTILHADOS
// =================================================================

// Componente reutilizável para os cards
interface InfoCardProps {
  data: InfoCardData;
  index: number;
  colorManager?: {
    getNextColor: () => ColorScheme;
  };
}

export function InfoCard({ data, index, colorManager }: InfoCardProps) {
  return (
    <motion.div
      className="flex items-center gap-2 p-2 rounded-lg bg-cyan-50 dark:bg-cyan-950/20 border border-cyan-200 dark:border-cyan-800"
      variants={cardVariants}
      whileHover="hover"
    >
      <div className="flex items-center justify-center bg-cyan-500 rounded p-0.5">
        <data.icon className="h-3 w-3 text-white" />
      </div>
      <div className="flex flex-col gap-0">
        <span className="text-[9px] text-cyan-700 dark:text-cyan-300 font-medium">
          {data.label}
        </span>
        <span className="text-[10px] font-semibold text-cyan-600 dark:text-cyan-400 leading-tight">
          {data.value}
        </span>
      </div>
    </motion.div>
  );
}
